import { ColDef, ICellRendererParams } from 'ag-grid-community';

import { FreshShipmentLink } from '@/components/common/links';
import { DonorFertilizationLink } from '@/components/common/links';
import { EXPORT_VALUES } from '@/constants';

import { BillableShippedLockRenderer } from '../components/BillableShippedLockRenderer';
import { BillingDropdown } from '../components/BillingDropdown';
import { BillingStatusRenderer } from '../components/BillingStatusRenderer';
import { FreshShipmentCombineButton } from '../components/FreshShipmentCombineButton';
import { FreshShipmentsPdfButton } from '../components/FreshShipmentsPdfButton';
import { FreshShipmentSplitDialog } from '../components/FreshShipmentSplitDialog';
import { FreshShipmentUndoButton } from '../components/FreshShipmentUndoButton';
import { FreshShipmentVerifiedRenderer } from '../components/FreshShipmentVerifiedRenderer';
import { PredictionCommentsDropdown } from '../components/PredictionCommentsDropdown';
import { ShipmentEstimationsDropdown } from '../components/ShipmentEstimationsDropdown';
import { ShipmentGroupsViewButton } from '../components/ShipmentGroupsViewButton';
import { TrackingNumberDropdown } from '../components/TrackingNumberDropdown';
import { VerifiedCellRenderer } from '../components/VerifiedCellRenderer';
import { DonorFertilizationLinkParams } from './types';
import { calculateTotalAllocatedEmbryos, calculateTotalEmbryos } from './utils';

export const shipmentGroupsColDefs: ColDef[] = [
  {
    wrapText: true,
    autoHeight: true,
    field: 'checkbox',
    headerName: '',
    headerTooltip: '',
    width: 50,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'shipGroupName',
    headerName: 'Ship Group Name',
    headerTooltip: 'Ship Group Name',
    width: 270,
  },

  {
    wrapText: true,
    autoHeight: true,
    field: 'fertDate',
    headerName: 'Fert Date',
    headerTooltip: 'Fert Date',
    width: 80,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'numMating',
    headerName: 'Matings',
    headerTooltip: 'Matings',
    width: 70,
  },
  { field: 'numTubes', headerName: 'Tubes', headerTooltip: 'Tubes', width: 70 },
  {
    cellRenderer: ShipmentGroupsViewButton,
    headerTooltip: '',
    width: 70,
  },
];

export const getFreshShipmentsColDefs = (): ColDef[] => {
  const colDefs: ColDef[] = [];

  colDefs.push({
    wrapText: true,
    autoHeight: true,
    field: 'checkbox',
    headerName: '',
    headerTooltip: '',
    width: 30,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
  });

  colDefs.push({
    headerName: '',
    cellRenderer: 'agGroupCellRenderer',
    width: 50,
    suppressMovable: true,
    lockPosition: true,
  });

  colDefs.push(
    {
      wrapText: true,
      autoHeight: true,
      field: 'name',
      headerName: 'Shipment Name',
      headerTooltip: 'Shipment Name',
      cellRenderer: FreshShipmentLink,
      cellRendererParams: (params: ICellRendererParams) => ({
        environmentId: params.context.me?.environmentId,
        freshShipmentId: params.data.freshShipmentId,
        name: params.data.name,
      }),
      width: 200,
    },

    {
      wrapText: true,
      autoHeight: true,
      field: 'shipmentNumber',
      headerName: 'FS Number',
      headerTooltip: 'FS Number',
      width: 80,
    },
    {
      wrapText: true,
      autoHeight: true,
      field: 'matingsCount',
      headerName: 'Matings',
      headerTooltip: 'Matings',
      width: 60,
    },
    {
      field: 'shippedEmbryosCount',
      headerName: 'Embryos',
      headerTooltip: 'Embryos',
      width: 60,
    },
    {
      wrapText: true,
      autoHeight: true,
      field: 'trackingNumber',
      headerName: 'Tracking Number',
      headerTooltip: 'Tracking Number',
      width: 160,
      editable: true,
      cellRenderer: TrackingNumberDropdown,
      cellRendererParams: (params: ICellRendererParams) => ({
        setValue: async (value: string) => {
          if (params.context.isTrackingNumberUpdateEnabled) {
            await params.context.handleUpdateFreshShipment({
              ...params,
              newValue: value,
            });
            return;
          }

          params.context.handleUpdateFreshShipment({
            ...params,
            newValue: value,
          });
        },
      }),
    },
    {
      wrapText: true,
      autoHeight: true,
      field: 'shipperId',
      headerName: 'Shipper ID',
      headerTooltip: 'Shipper ID',
      width: 80,
      editable: true,

      cellClassRules: {
        'fresh-shipment__editable-cell': (params) =>
          params.column.isCellEditable(params.node),
      },
    },
    {
      cellRenderer: FreshShipmentsPdfButton,
      cellRendererParams: (params: ICellRendererParams) => ({
        shipmentNumber: params.data.shipmentNumber,
        freshShipmentId: params.data.freshShipmentId,
        embryos: params.data.shippedEmbryosCount,
        accountNumber: params.data.accountNumber,
      }),
      headerTooltip: '',
      width: 120,
      headerName: 'PDF',
    },
    {
      headerName: '',
      headerTooltip: '',
      cellRenderer: (params: ICellRendererParams) => {
        if (params.context.isCsr) {
          return FreshShipmentUndoButton({ params });
        }
        return null;
      },
      width: 80,
    },
    {
      width: 100,
      headerName: '',
      headerTooltip: '',
      cellRenderer: (params: ICellRendererParams) => {
        if (params.context.isCsr) {
          return FreshShipmentCombineButton({ params });
        }
        return null;
      },
    },
  );

  colDefs.push({
    field: 'verified',
    headerName: 'Verified',
    headerTooltip: 'Fresh Shipment Verified',
    width: 60,
    cellRenderer: FreshShipmentVerifiedRenderer,
  });

  return colDefs;
};

export const signedOffGridColDefs: ColDef[] = [
  {
    field: 'tubeNumber',
    headerName: 'Tube #',
    headerTooltip: 'Tube #',
    width: 30,
  },
  {
    field: 'inc1',
    headerName: 'Ship Group Master',
    headerTooltip: 'Ship Group Master',
    wrapText: true,
    autoHeight: true,
    width: 70,
  },
  {
    field: 'accountNumber',
    headerName: 'Account',
    headerTooltip: 'Account',
    width: 50,
  },
  {
    field: 'siteName',
    headerName: 'OPU Site',
    headerTooltip: 'OPU Site',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'donorName',
    headerName: 'Donor',
    headerTooltip: 'Donor',
    width: 80,
    cellRenderer: DonorFertilizationLink,
    cellRendererParams: (params: DonorFertilizationLinkParams) => ({
      environmentId: params.context.me?.environmentId,
      fertilizationId: params.data.fertilizationId,
      donorName: params.data.donorName,
    }),
    cellStyle({ data }) {
      if (data?.isDuplicate) {
        return { backgroundColor: 'yellow', fontWeight: 'bold' };
      }

      return { backgroundColor: '', fontWeight: 'bold' };
    },
  },

  {
    field: 'donorBreed',
    headerName: 'D. Breed',
    headerTooltip: 'D. Breed',
    width: 40,
  },
  {
    field: 'sireName',
    headerName: 'Sire',
    headerTooltip: 'Sire',
    width: 60,
  },
  {
    field: 'sireBreed',
    headerName: 'S. Breed',
    headerTooltip: 'S. Breed',
    width: 50,
  },
  {
    field: 'colCode',
    headerName: 'Col Code',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'semenTypeName',
    headerName: 'Semen Type',
    headerTooltip: 'Semen Type',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'priority',
    headerName: 'Priority',
    headerTooltip: 'Priority',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'ivcNumber',
    headerName: 'IVC Num',
    headerTooltip: 'IVC Num',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'cleaved',
    headerName: 'Clvd',
    headerTooltip: 'Clvd',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'predictedGood',
    headerName: 'Qual',
    headerTooltip: 'Qual',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'predictedVeryLow',
    headerName: 'Low',
    headerTooltip: 'Low',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'origShipped',
    headerName: 'Ship',
    headerTooltip: 'Ship',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'origHeldAtTog',
    headerName: 'Hold',
    headerTooltip: 'Hold',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'shipped',
    headerName: 'CSR Shp Client',
    headerTooltip: 'CSR Shp Client',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'shippedTog',
    headerName: 'CSR Shp TOG',
    headerTooltip: 'CSR Shp TOG',
    width: 30,
  },
  {
    field: 'heldAtTogToFreeze',
    headerName: 'CSR Frz',
    headerTooltip: 'CSR Frz',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'heldAtTogFreezeTransferLowGrade',
    headerName: 'CSR Frz/Trf LG',
    headerTooltip: 'CSR Frz/Trf LG',
    width: 30,
  },

  {
    wrapText: true,
    autoHeight: true,
    field: 'heldAtTogForTransfer',
    headerName: 'CSR Trf',
    headerTooltip: 'CSR Trf',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'day5esttrans',
    headerName: 'Estimate',
    headerTooltip: 'Estimate',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'numMating',
    headerName: 'No Matings',
    headerTooltip: 'No Matings',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'billIncubator',
    headerName: 'Bill Inc',
    headerTooltip: 'Bill Inc',
    width: 30,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'billShipping',
    headerName: 'Bill Ship',
    headerTooltip: 'Bill Ship',
    width: 30,
  },
  {
    field: 'shippedBillable',
    headerName: 'Billable Shipped',
    headerTooltip: 'Billable Shipped',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    headerName: 'Export',
    field: 'exportName',
    headerTooltip: 'Export',
    width: 40,
    cellStyle: (params) => {
      const exportValue = params.data?.exportName as string;

      if (exportValue === EXPORT_VALUES.NON_EU) {
        return {
          backgroundColor: 'var(--tog-red)',
          color: '',
        };
      }

      if (exportValue === EXPORT_VALUES.EU) {
        return {
          backgroundColor: 'var(--tog-orange)',
          color: '',
        };
      }

      if (exportValue === EXPORT_VALUES.AU) {
        return {
          backgroundColor: 'var(--tog-yellow)',
          color: '',
        };
      }

      if (exportValue === EXPORT_VALUES.SA) {
        return {
          backgroundColor: 'var(--tog-maroon)',
          color: 'white',
        };
      }

      if (exportValue === EXPORT_VALUES.UK) {
        return {
          backgroundColor: 'var(--tog-light-blue)',
          color: '',
        };
      }

      return null;
    },
  },

  {
    field: 'partnerships',
    headerName: 'Partnerships',
    headerTooltip: 'Partnerships',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'noEmbryosProduced',
    headerName: 'NEP',
    headerTooltip: 'No Emb Prod',
    width: 30,
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    valueGetter: (params) => {
      return params.data?.noEmbryosProduced !== null ? true : false;
    },
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'contaminantPresent',
    headerName: 'Contaminant Present',
    headerTooltip: 'Contaminant Present',
    width: 30,
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    valueGetter: (params) => {
      return params.data?.contaminantPresent !== null ? true : false;
    },
  },

  {
    wrapText: true,
    autoHeight: true,
    field: 'loadedInStraws',
    headerName: 'Load In Straws',
    headerTooltip: 'Load In Straws',
    width: 50,
  },
  {
    field: 'programName',
    headerName: 'Program Name',
    headerTooltip: 'Program Name',
    width: 70,
  },
  {
    field: 'comments',
    headerName: 'Comments',
    headerTooltip: 'Comments',
    wrapText: true,
    autoHeight: true,
    width: 120,
  },
  {
    field: 'csrComment',
    headerName: 'CSR Comment',
    headerTooltip: 'CSR Comment',
    width: 73,
    hide: true,
  },
  {
    field: 'inc2',
    headerName: 'Ship Group Master 2',
    headerTooltip: 'Ship Group Master 2',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'shipped2',
    headerName: 'Ship Client 2',
    headerTooltip: 'Ship Client 2',
    width: 50,
  },
  {
    field: 'shippedTog2',
    headerName: 'Ship TOG 2',
    headerTooltip: 'Ship TOG 2',
    width: 50,
  },
];

export const ivfViewGridColDefs: ColDef[] = [
  {
    headerName: '',
    headerTooltip: '',
    width: 30,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
  },
  {
    field: 'plateNumber',
    headerName: 'Plate',
    headerTooltip: 'Plate',
    width: 42,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'tubeNumber',
    headerName: 'Tube #',
    headerTooltip: 'Tube #',
    width: 40,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'csr',
    headerName: 'CSR',
    headerTooltip: 'CSR',
    width: 63,
  },

  {
    field: 'accountNumber',
    headerName: 'Account',
    headerTooltip: 'Account',
    width: 63,
  },
  {
    field: 'siteName',
    headerName: 'OPU Site',
    headerTooltip: 'OPU Site',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: false,
    field: 'donorName',
    headerName: 'Donor',
    headerTooltip: 'Donor',
    width: 60,
    cellRenderer: DonorFertilizationLink,
    cellRendererParams: (params: DonorFertilizationLinkParams) => ({
      environmentId: params.context.me?.environmentId,
      fertilizationId: params.data.fertilizationId,
      donorName: params.data.donorName,
    }),
    cellStyle({ data }) {
      if (data?.isDuplicate) {
        return { backgroundColor: 'yellow', fontWeight: 'bold' };
      }

      return { backgroundColor: '', fontWeight: 'bold' };
    },
  },

  {
    wrapText: true,
    autoHeight: true,
    field: 'donorBreed',
    headerName: 'D. Breed',
    headerTooltip: 'D. Breed',
    width: 50,
  },
  {
    field: 'sireName',
    headerName: 'Sire',
    headerTooltip: 'Sire',
    width: 50,
  },
  {
    field: 'sireBreed',
    headerName: 'S. Breed',
    headerTooltip: 'S. Breed',
    width: 50,
  },
  {
    field: 'colCode',
    headerName: 'Col Code',
    width: 50,
  },
  {
    field: 'semenTypeName',
    headerName: 'Semen Type',
    headerTooltip: 'Semen Type',
    width: 50,
  },
  {
    field: 'ivcNumber',
    headerName: 'IVC #',
    headerTooltip: 'IVC #',
    width: 47,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'cleaved',
    headerName: 'Clvd',
    headerTooltip: 'Clvd',
    width: 50,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'predictedGood',
    headerName: 'Qual',
    headerTooltip: 'Qual',
    width: 50,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'predictedVeryLow',
    headerName: 'Low',
    headerTooltip: 'Low',
    width: 50,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    field: 'origShipped',
    headerName: 'Ship',
    headerTooltip: 'Ship',
    width: 50,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueGetter: (params) => {
      if (
        params.data?.origShipped !== undefined &&
        params.data?.origShipped !== null
      ) {
        return params.data.origShipped;
      }

      if (params.context.d5 === 'true') {
        return params.data?.origShipped;
      }

      const shipped =
        parseInt(params.data?.predictedGood || '0') +
        parseInt(params.data?.predictedVeryLow || '0');
      if (
        params.data?.origShipped !== shipped &&
        (params.data?.predictedGood || params.data?.predictedVeryLow)
      ) {
        params.context.updateFertilization({
          wrapText: true,
          autoHeight: true,
          id: params.data.fertilizationId,
          field: 'origShipped',
          value: shipped.toString(),
          updatePredictedComments: false,
        });
        return shipped;
      }
      return params.data?.origShipped;
    },
  },

  {
    field: 'origHeldAtTog',
    headerName: 'Held',
    headerTooltip: 'Held',
    width: 50,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    headerName: '',
    field: 'day5esttrans',
    editable: true,
    headerTooltip: 'Estimate',
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    headerComponent: ShipmentEstimationsDropdown,
    width: 110,
  },

  {
    field: 'priority',
    headerName: 'Priority',
    headerTooltip: 'Priority',
    width: 50,
  },
  {
    wrapText: true,
    autoHeight: true,
    headerName: 'Export',
    field: 'exportName',
    headerTooltip: 'Export',
    width: 50,
    cellStyle: (params) => {
      const exportValue = params.data?.exportName as string;

      if (exportValue === 'Yes - Non EU') {
        return {
          backgroundColor: 'var(--tog-red)',
          color: '',
        };
      }

      if (exportValue === 'EU') {
        return {
          backgroundColor: 'var(--tog-orange)',
          color: '',
        };
      }

      if (exportValue === 'AU') {
        return {
          backgroundColor: 'var(--tog-yellow)',
          color: '',
        };
      }

      if (exportValue === 'SA') {
        return {
          backgroundColor: 'var(--tog-maroon)',
          color: 'white',
        };
      }

      if (exportValue === 'UK') {
        return {
          backgroundColor: 'var(--tog-light-blue)',
          color: '',
        };
      }

      return null;
    },
  },

  {
    field: 'partnerships',
    headerName: 'Partnerships',
    headerTooltip: 'Partnerships',
    width: 70,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'loadedInStraws',
    headerName: 'Load In Straws',
    headerTooltip: 'Load In Straws',
    width: 50,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'noEmbryosProduced',
    headerName: 'No Embs Prod',
    headerTooltip: 'No Embs Prod',
    width: 50,
    editable: true,
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueGetter: (params) => {
      return params.data?.noEmbryosProduced !== null ? true : false;
    },
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'contaminantPresent',
    headerName: 'Contaminant Present',
    headerTooltip: 'Contaminant Present',
    width: 55,
    editable: true,
    cellRenderer: 'agCheckboxCellRenderer',
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    cellEditor: 'agCheckboxCellEditor',
    valueGetter: (params) => {
      return params.data?.contaminantPresent !== null ? true : false;
    },
  },
  {
    field: 'programName',
    headerName: 'Program Name',
    headerTooltip: 'Program Name',
    width: 70,
  },
  {
    field: 'predictedBy',
    headerName: 'Predicted By',
    headerTooltip: 'Predicted By',
    width: 70,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'freshShipmentComments',
    headerName: 'Prediction Comments',
    headerTooltip: 'Prediction Comments',
    width: 115,
    editable: true,

    cellRenderer: PredictionCommentsDropdown,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'ivfComment',
    headerName: 'Embryo Comment',
    headerTooltip: 'Embryo Comment',
    width: 100,
    editable: true,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      comment: () => true,
    },
  },

  {
    wrapText: true,
    autoHeight: true,
    field: 'csrComment',
    headerName: 'CSR Comment',
    headerTooltip: 'CSR Comment',
    width: 100,
    hide: false,
  },
  {
    field: 'comments',
    headerName: 'Comments',
    headerTooltip: 'Comments',
    wrapText: true,
    autoHeight: true,
    width: 120,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'shipped',
    headerName: 'CSR Shp Client',
    headerTooltip: 'CSR Shp Client',
    width: 55,
    hide: true,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'shippedTog',
    headerName: 'CSR Shp TOG',
    headerTooltip: 'CSR Shp TOG',
    width: 55,
    hide: true,
  },
  {
    field: 'heldAtTogToFreeze',
    headerName: 'CSR Frz',
    headerTooltip: 'CSR Frz',
    width: 55,
    hide: true,
  },
  {
    wrapText: true,
    autoHeight: true,
    field: 'heldAtTogFreezeTransferLowGrade',
    headerName: 'CSR Frz/Trf LG',
    headerTooltip: 'CSR Frz/Trf LG',
    width: 55,
    hide: true,
  },

  {
    wrapText: true,
    autoHeight: true,
    field: 'heldAtTogForTransfer',
    headerName: 'CSR Trf',
    headerTooltip: 'CSR Trf',
    width: 55,
    hide: true,
  },
  {
    field: 'embryos',
    headerName: 'Embryos',
    headerTooltip: 'Embryos',
    width: 55,
  },
];

export const csrViewGridColDefs: ColDef[] = [
  {
    headerName: '',
    headerTooltip: '',
    width: 30,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
  },

  {
    headerName: 'Tube No',
    headerTooltip: 'Tube No',
    field: 'tubeNumber',
    width: 40,
  },
  {
    headerName: 'Incubator',
    field: 'inc1',
    headerTooltip: 'Incubator',
    width: 65,
  },

  {
    headerName: 'CSR',
    field: 'csr',
    headerTooltip: 'CSR',
    width: 65,
  },
  {
    headerName: 'Account',

    field: 'accountNumber',
    headerTooltip: 'Account',
    width: 65,
  },
  {
    headerName: 'OPU Site',
    field: 'siteName',
    headerTooltip: 'OPU Site',
    width: 50,
  },
  {
    headerName: '(Breed) -- Donor',
    field: 'breedDonor',
    headerTooltip: 'Donor',
    width: 65,
    cellRenderer: DonorFertilizationLink,
    cellRendererParams: (params: DonorFertilizationLinkParams) => ({
      environmentId: params.context.me?.environmentId,
      fertilizationId: params.data.fertilizationId,
      donorName: params.data.breedDonor,
    }),
    cellStyle({ data }) {
      if (data?.isDuplicate) {
        return { backgroundColor: 'yellow', fontWeight: 'bold' };
      }

      return { backgroundColor: '', fontWeight: 'bold' };
    },
  },
  {
    headerName: '(Breed) -- Sire',
    field: 'breedSire',
    width: 75,
    cellStyle: () => {
      return { backgroundColor: '', fontWeight: 'bold' };
    },
  },
  {
    headerName: 'Semen Type',

    field: 'semenTypeName',
    headerTooltip: 'Semen Type',
    width: 60,
  },
  {
    headerName: 'IVC Num',
    field: 'ivcNumber',
    headerTooltip: 'IVC Num',
    width: 50,
  },
  {
    headerName: 'Clvd',

    field: 'cleaved',
    headerTooltip: 'Clvd',
    width: 50,
  },
  {
    headerName: 'Qual',

    field: 'predictedGood',
    headerTooltip: 'Qual',
    width: 50,
  },
  {
    headerName: 'Low',

    field: 'predictedVeryLow',
    headerTooltip: 'Low',
    width: 50,
  },
  {
    headerName: 'Ship',
    field: 'origShipped',
    headerTooltip: 'Ship',
    width: 50,
  },
  {
    headerName: 'Held',
    field: 'origHeldAtTog',
    headerTooltip: 'Held',
    width: 50,
  },
  {
    headerName: 'CSR Ship Client',
    field: 'shipped',
    headerTooltip: 'CSR Ship Client',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      'fresh-shipment__error': (params) => {
        const totalAllocated = calculateTotalAllocatedEmbryos(params.data);
        const totalEmbryos = calculateTotalEmbryos(params.data);

        if (params.context?.d5) {
          return totalAllocated > params.data?.origShipped;
        }
        return totalAllocated > totalEmbryos;
      },
    },
  },
  {
    headerName: 'CSR Ship TOG',
    field: 'shippedTog',
    headerTooltip: 'CSR Ship TOG',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },

    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      'fresh-shipment__error': (params) => {
        const totalAllocated = calculateTotalAllocatedEmbryos(params.data);
        const totalEmbryos = calculateTotalEmbryos(params.data);
        if (params.context?.d5) {
          return totalAllocated > params.data?.origShipped;
        }
        return totalAllocated > totalEmbryos;
      },
    },
  },
  {
    headerName: 'CSR Freeze',
    field: 'heldAtTogToFreeze',
    headerTooltip: 'CSR Freeze',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      'fresh-shipment__error': (params) => {
        const totalAllocated = calculateTotalAllocatedEmbryos(params.data);
        const totalEmbryos = calculateTotalEmbryos(params.data);
        if (params.context?.d5) {
          return totalAllocated > params.data?.origShipped;
        }

        return totalAllocated > totalEmbryos;
      },
    },
  },
  {
    headerName: 'CSR Frz/Trf LG',
    field: 'heldAtTogFreezeTransferLowGrade',
    headerTooltip: 'CSR Frz/Trf LG',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      'fresh-shipment__error': (params) => {
        const totalAllocated = calculateTotalAllocatedEmbryos(params.data);
        const totalEmbryos = calculateTotalEmbryos(params.data);
        if (params.context?.d5) {
          return totalAllocated > params.data?.origShipped;
        }
        return totalAllocated > totalEmbryos;
      },
    },
  },
  {
    headerName: 'CSR TRF',
    field: 'heldAtTogForTransfer',
    headerTooltip: 'CSR TRF',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      'fresh-shipment__error': (params) => {
        const totalAllocated = calculateTotalAllocatedEmbryos(params.data);
        const totalEmbryos = calculateTotalEmbryos(params.data);
        if (params.context?.d5) {
          return totalAllocated > params.data?.origShipped;
        }
        return totalAllocated > totalEmbryos;
      },
    },
  },
  {
    headerName: 'Bill Inc',
    field: 'billIncubator',
    headerTooltip: 'Bill Inc',
    width: 73,
    cellRenderer: BillingDropdown,
    cellRendererParams: (params: ICellRendererParams) => ({
      setValue: (value: string) => {
        params.context.handleUpdateFertilization({
          ...params,
          newValue: value,
        });
        return value;
      },
    }),
    editable: false,
  },
  {
    headerName: 'Bill Ship',
    field: 'billShipping',
    headerTooltip: 'Bill Ship',
    width: 73,
    cellRenderer: BillingDropdown,
    cellRendererParams: (params: ICellRendererParams) => ({
      setValue: (value: string) => {
        params.context.handleUpdateFreshShipment({
          ...params,
          newValue: value,
        });
        return value;
      },
      options: [
        { text: '--', value: null },
        { text: 'Yes', value: 'true' },
        { text: 'No', value: 'false' },
      ],
    }),
    editable: false,
  },
  {
    headerName: 'Bill Shipped',
    field: 'shippedBillable',
    headerTooltip: 'Bill Shipped',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    headerName: 'Load In Straws',
    field: 'loadedInStraws',
    headerTooltip: 'Load In Straws',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    headerName: 'Estimate',
    field: 'day5esttrans',
    headerTooltip: 'Estimate',
    width: 50,
  },
  {
    headerName: 'Priority',
    field: 'priority',
    headerTooltip: 'Priority',
    width: 50,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellEditorParams: {
      maxLength: 3,
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
  },
  {
    headerName: 'Export',
    field: 'exportName',
    headerTooltip: 'Export',
    width: 50,
    cellStyle: (params) => {
      const exportValue = params.data?.exportName as string;

      if (exportValue === 'Yes - Non EU') {
        return {
          backgroundColor: 'var(--tog-red)',
          color: '',
        };
      }

      if (exportValue === 'EU') {
        return {
          backgroundColor: 'var(--tog-orange)',
          color: '',
        };
      }

      if (exportValue === 'AU') {
        return {
          backgroundColor: 'var(--tog-yellow)',
          color: '',
        };
      }

      if (exportValue === 'SA') {
        return {
          backgroundColor: 'var(--tog-maroon)',
          color: 'white',
        };
      }

      if (exportValue === 'UK') {
        return {
          backgroundColor: 'var(--tog-light-blue)',
          color: '',
        };
      }

      return null;
    },
  },
  {
    headerName: 'Partnerships',
    field: 'partnerships',
    headerTooltip: 'Partnerships',
    width: 50,
  },
  {
    headerName: 'NEP',
    field: 'noEmbryosProduced',
    headerTooltip: 'NEP',
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    width: 50,
    valueGetter: (params) => {
      return params.data?.noEmbryosProduced !== null ? true : false;
    },
  },
  {
    field: 'contaminantPresent',
    headerName: 'Contaminant Present',
    headerTooltip: 'Contaminant Present',
    width: 50,
    cellRenderer: 'agCheckboxCellRenderer',
    cellEditor: 'agCheckboxCellEditor',
    valueGetter: (params) => {
      return params.data?.contaminantPresent !== null ? true : false;
    },
  },
  {
    headerName: 'Predicted By',
    field: 'predictedBy',
    headerTooltip: 'Predicted By',
    width: 63,
  },
  {
    field: 'programName',
    headerName: 'Program Name',
    headerTooltip: 'Program Name',
    width: 70,
  },
  {
    headerName: 'Comments',
    field: 'freshShipmentComments',
    headerTooltip: 'Comments',
    width: 100,
  },
  {
    headerName: 'Embryo Comments',
    field: 'ivfComment',
    headerTooltip: 'Embryo Comments',
    width: 70,
  },
  {
    headerName: 'CSR Comment',
    field: 'csrComment',
    headerTooltip: 'Embryo Comments',
    width: 70,
    editable: true,
    cellEditor: 'agTextCellEditor',
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
      comment: () => true,
    },
  },
  {
    field: 'comments',
    headerName: 'Comments',
    headerTooltip: 'Comments',
    wrapText: true,
    autoHeight: true,
    width: 120,
  },
  {
    headerName: 'Total Embryos',
    field: '',
    width: 70,
    valueGetter: (params) => {
      const shipped =
        parseInt(params.data?.predictedGood || '0') +
        parseInt(params.data?.predictedVeryLow || '0');
      if (
        params.data?.origShipped !== shipped &&
        (params.data?.predictedGood || params.data?.predictedVeryLow)
      ) {
        return shipped;
      }
    },
  },
  {
    headerName: 'Split',
    field: '',
    width: 70,
    cellRenderer: FreshShipmentSplitDialog,
    cellRendererParams: (params: ICellRendererParams) => ({
      params,
    }),
    cellStyle: (params) => {
      const split =
        params.data.inc2Id !== null &&
        params.data.inc1Id !== params.data.inc2Id;
      if (split) {
        return { backgroundColor: 'var(--tog-red)' };
      }
    },
  },
];

export type BillingStatus =
  | 'incomplete'
  | 'notReconciled'
  | 'reconciled'
  | 'verified';

export interface BillableShippedRow {
  fertilizationId: string;
  mating: string;
  shipClient: number | null;
  clientTrf: number | null;
  clientFrz: number | null;
  clientDiscard: number | null;
  billableShipped: number | null;
  billableShippedOverridden: boolean;
  verified: boolean;
  isReconciled: boolean;
  reconciliationDiff: number;
  billingStatus: BillingStatus;
  saveStatus?: 'saving' | 'saved' | 'error';
  sireRegistrationNumber?: string | null;
  sireBreedName?: string | null;
  donorRegistrationNumber?: string | null;
  donorBreedName?: string | null;
  programName?: string | null;
  naab?: string | null;
}

export interface BillableShippedGridContext {
  onVerifyToggle: (fertilizationId: string, currentlyVerified: boolean) => void;
  onLockToggle: (fertilizationId: string) => void;
  unlockedIds: Set<string>;
}

export const billableShippedColDefs: ColDef[] = [
  {
    field: 'checkbox',
    headerName: '',
    headerTooltip: '',
    width: 50,
    checkboxSelection: true,
    headerCheckboxSelection: true,
    suppressMovable: true,
    lockPosition: true,
  },
  {
    field: 'mating',
    headerName: 'Mating',
    headerTooltip: 'Mating',
    width: 170,
  },
  {
    field: 'shipClient',
    headerName: 'Ship Client',
    headerTooltip: 'Ship Client',
    width: 75,
    type: 'numericColumn',
  },
  {
    field: 'clientTrf',
    headerName: 'Client Trf',
    headerTooltip: 'Client Trf',
    width: 75,
    type: 'numericColumn',
    editable: (params) => !params.data?.verified,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueFormatter: (params) =>
      params.value == null ? '' : String(params.value),
    valueParser: (params) => {
      if (params.newValue === '' || params.newValue == null) return null;
      const n = Number(params.newValue);
      return Number.isNaN(n) ? params.oldValue : n;
    },
  },
  {
    field: 'clientFrz',
    headerName: 'Client Frz',
    headerTooltip: 'Client Frz',
    width: 75,
    type: 'numericColumn',
    editable: (params) => !params.data?.verified,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueFormatter: (params) =>
      params.value == null ? '' : String(params.value),
    valueParser: (params) => {
      if (params.newValue === '' || params.newValue == null) return null;
      const n = Number(params.newValue);
      return Number.isNaN(n) ? params.oldValue : n;
    },
  },
  {
    field: 'clientDiscard',
    headerName: 'Client Discard',
    headerTooltip: 'Client Discard',
    width: 75,
    type: 'numericColumn',
    editable: (params) => !params.data?.verified,
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueFormatter: (params) =>
      params.value == null ? '' : String(params.value),
    valueParser: (params) => {
      if (params.newValue === '' || params.newValue == null) return null;
      const n = Number(params.newValue);
      return Number.isNaN(n) ? params.oldValue : n;
    },
  },
  {
    field: 'billableShipped',
    headerName: 'Billable Shipped',
    headerTooltip: 'Billable Shipped',
    width: 75,
    type: 'numericColumn',
    editable: (params) => {
      const context = params.context as BillableShippedGridContext;
      const isUnlocked =
        context?.unlockedIds?.has(params.data?.fertilizationId ?? '') ?? false;
      return isUnlocked && !params.data?.verified;
    },
    cellClassRules: {
      'fresh-shipment__editable-cell': (params) =>
        params.column.isCellEditable(params.node),
    },
    valueFormatter: (params) =>
      params.value == null ? '' : String(params.value),
    valueParser: (params) => {
      if (params.newValue === '' || params.newValue == null) return null;
      const n = Number(params.newValue);
      if (isNaN(n) || n < 0 || !Number.isInteger(n)) return params.oldValue;
      return n;
    },
  },
  {
    headerName: '',
    width: 40,
    suppressMovable: true,
    sortable: false,
    cellRenderer: BillableShippedLockRenderer,
  },
  {
    field: 'verified',
    headerName: 'Verified',
    headerTooltip: 'Verified',
    width: 60,
    cellRenderer: VerifiedCellRenderer,
    equals: () => false,
  },
  {
    field: 'saveStatus',
    headerName: '',
    headerTooltip: '',
    width: 36,
    sortable: false,
    suppressMovable: true,
    cellRenderer: (params: { value: BillableShippedRow['saveStatus'] }) => {
      if (params.value === 'saving') return '⋯';
      if (params.value === 'saved') return '✓';
      if (params.value === 'error') return '⚠';
      return null;
    },
    cellClass: (params: { value: BillableShippedRow['saveStatus'] }) => {
      if (params.value === 'saved') return 'text-green-600';
      if (params.value === 'error') return 'text-red-600';
      return 'text-gray-400';
    },
  },
  {
    field: 'billingStatus',
    headerName: 'Status',
    headerTooltip: 'Billing Status',
    width: 150,
    sortable: false,
    cellRenderer: BillingStatusRenderer,
  },
];
